import {
  Configuration,
  INetworkModule,
  IPublicClientApplication,
  NetworkResponse,
} from '@azure/msal-browser';

const AUTHORIZE_PATH = '/oauth2/v2.0/authorize';
const DISCOVERY_PATH = '/v2.0/.well-known/openid-configuration';

interface InlinedDiscovery {
  /** Lower-cased discovery URL this document belongs to. */
  readonly url: string;
  readonly document: Record<string, unknown>;
}

/**
 * Removes `auth.authorityMetadata` from the MSAL configuration so it is not
 * applied to every authority.
 *
 * MSAL uses that single config field for *any* request authority and prefers
 * it over network discovery. The SSR server (meta-app) and the config
 * generator (klia-app) inline the **sign-in** policy's discovery document
 * there, so a `loginRedirect` with the password-reset authority was sent to
 * the sign-in `authorize` endpoint — B2C signed the user straight back in and
 * "Passwort ändern" never showed its form (BDA-6509).
 *
 * Returns the configuration without the field plus the parsed document,
 * which {@link serveInlinedDiscovery} then answers only for its own policy.
 */
export function extractAuthorityMetadata(configuration: Configuration): {
  configuration: Configuration;
  discovery: InlinedDiscovery | null;
} {
  const { authorityMetadata, ...auth } = configuration.auth;
  if (!authorityMetadata) return { configuration, discovery: null };
  return {
    configuration: { ...configuration, auth },
    discovery: parseDiscovery(authorityMetadata),
  };
}

/**
 * Answers MSAL's discovery request for the inlined document's own authority
 * from memory, so the sign-in policy still boots without the B2C round trip
 * (`Cache-Control: no-store`, 176 ms – 4.4 s observed). Every other authority
 * — password reset, any other policy — and every other request goes to
 * MSAL's own network client unchanged.
 *
 * The instance's network client is the object MSAL reads for discovery and
 * token calls alike, so wrapping its GET covers every interaction path,
 * including token redemption after the redirect returns.
 */
export function serveInlinedDiscovery(
  instance: IPublicClientApplication,
  discovery: InlinedDiscovery | null,
): void {
  if (!discovery) return;
  const network: INetworkModule =
    instance.getConfiguration().system.networkClient;
  const sendGetRequestAsync = network.sendGetRequestAsync.bind(network);
  network.sendGetRequestAsync = <T>(
    ...args: Parameters<INetworkModule['sendGetRequestAsync']>
  ): Promise<NetworkResponse<T>> => {
    const [url] = args;
    if (url.split('?')[0].toLowerCase() === discovery.url) {
      return Promise.resolve({
        headers: {},
        body: discovery.document as T,
        status: 200,
      });
    }
    return sendGetRequestAsync<T>(...args);
  };
}

function parseDiscovery(authorityMetadata: string): InlinedDiscovery | null {
  try {
    const document: unknown = JSON.parse(authorityMetadata);
    const endpoint = isRecord(document)
      ? document['authorization_endpoint']
      : undefined;
    if (
      isRecord(document) &&
      typeof endpoint === 'string' &&
      endpoint.toLowerCase().endsWith(AUTHORIZE_PATH)
    ) {
      const authority = endpoint.slice(0, -AUTHORIZE_PATH.length).toLowerCase();
      return { url: `${authority}${DISCOVERY_PATH}`, document };
    }
  } catch {
    // Fall through: MSAL discovers the authority over the network instead.
  }
  console.warn(
    'Ignoring inlined authority metadata without a usable authorization_endpoint',
  );
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
